import { Layout, Dropdown, Button } from "antd";
import { Link, useLocation } from "react-router-dom";
import useGlobalConfig from "@/store/global";
import { getManufacturerByProgram } from "@/libs/util";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useQueryCompassionateRequests } from "@/hooks/useQueryCompassionateRequests";
import { MenuOutlined } from "@ant-design/icons";
import { programLabels } from "@/config";

const { Header } = Layout;

type ProgramName = keyof typeof programLabels;

const PATHS_WITH_BUTTONS = [
  "/patients",
  "/assistance_requests",
  "/requests",
  "/provider_users",
];

const PATHS_WITHOUT_LOGIN_BUTTON = ["/patient_support"];

const LayoutHeader = () => {
  const {
    program,
    themeColor,
    programLogo,
    manufacturerLogo,
    updateMeData,
  } = useGlobalConfig();
  const { name } = program;

  const route = useLocation();
  const navigate = useNavigate();

  const prescribingLink = programLabels[name as ProgramName]?.prescribingLink;
  const isOctaPharma = getManufacturerByProgram(name)?.indexOf("Octapharma") !== -1;

  const { data: compassionateRequests = [] } = useQueryCompassionateRequests("medmonk_submitted");

  const showFreeTrialRequestNav = (name === "nuwiq" || name === "wilate")
  const showCompassionateRequestNav = compassionateRequests.length > 0;

  const showButtons = useMemo(() => {
    return PATHS_WITH_BUTTONS.some((path) =>
      route.pathname.includes(path),
    );
  }, [route.pathname, name]);

  return (
    <>
      <Header
        style={{
          background: isOctaPharma ? "#fff" : "#cccccc",
          padding: 0,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "54px",
        }}
      >
        <div className="flex items-center h-full pl-5">
          <img
            className="pointer-events-none select-none h-[22px]"
            src={manufacturerLogo}
          />
        </div>
      </Header>
      <Header
        style={{
          background: themeColor,
          padding: 0,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <div className="flex justify-end items-center h-full px-5">
          <Dropdown className="md:hidden" menu={{
            items: [
              {
                label: <Link to="/payments">Payments</Link>,
                key: "payments",
              },
              {
                label: <Link to="/info">Billing Information</Link>,
                key: "billing-information",
              },
              {
                label: <Link to="/contact">Contact Us</Link>,
                key: "contact-us",
              },
              ...(prescribingLink ? [{
                label: <Link to={prescribingLink} target="_blank">Full Prescribing Information</Link>,
                key: "full-prescribing-information",
              }] : []),
            ],
          }}>
            <Button type='text' icon={<MenuOutlined className="text-white text-xl" />} className="text-white text-xl" />
          </Dropdown>
          <div className="w-full hidden md:flex items-center justify-center md:justify-end space-x-4">
            <Link to="/payments" style={{ color: "white" }}>
              Payments
            </Link>
            <Link to="/info" style={{ color: "white" }}>
              Billing Information
            </Link>
            <Link to="/contact" style={{ color: "white" }}>
              Contact Us
            </Link>
            {prescribingLink && (
              <Link
                target="_blank"
                to={prescribingLink}
                style={{ color: "white" }}
              >
                Full Prescribing Information
              </Link>
            )}
          </div>
        </div>
      </Header>
      <div className="mx-auto flex max-w-screen-xl flex-col justify-between gap-y-4 px-5 py-4 md:py-10 md:flex-row md:items-center md:gap-y-0">
        <div style={{ backgroundColor: "#FFFFFF" }}>
          <a
            href={
              localStorage.getItem("Authorization")
                ? "/patients"
                : "/"
            }
          >
            <img
              className="pointer-events-none select-none h-[70px] md:h-[100px]"
              src={programLogo}
              alt="Medmonk Logo"
            />
          </a>
        </div>

        {localStorage.getItem("Authorization") ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {isOctaPharma && showButtons ? (
              <div className="mr-4 flex items-center overflow-hidden rounded-md">
                <button
                  className={"border-r-2 border-white px-4 py-2 text-white".concat(
                    route.pathname === "/patients"
                      ? "text-white brightness-[85%]"
                      : "",
                  )}
                  style={{ backgroundColor: themeColor }}
                  onClick={() => navigate("/patients")}
                >
                  Copay Assistance
                </button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        label: (
                          <Link to="/requests/special-requests">
                            BI Requests
                          </Link>
                        ),
                        key: "special-requests",
                      },
                      showFreeTrialRequestNav ? {
                        label: (
                          <Link to="/requests/free_trial_requests">
                            Free Trial Requests
                          </Link>
                        ),
                        key: "free-trial-requests",
                      } : null,
                      showCompassionateRequestNav ? {
                        label: (
                          <Link to="/requests/compassionate_access_requests">
                            Compassionate Requests
                          </Link>
                        ),
                        key: "compassionate-requests",
                      } : null,
                    ].filter(Boolean),
                  }}
                  placement="bottomLeft"
                  arrow={{ pointAtCenter: true }}
                >
                  <button
                    className={"border-r-2 border-white px-4 py-2 text-white".concat(
                      route.pathname.includes("/requests")
                        ? "text-white brightness-[85%]"
                        : "",
                    )}
                    style={{ backgroundColor: themeColor }}
                  >
                    Special Requests
                  </button>
                </Dropdown>
                <button
                  className={"border-white px-4 py-2 text-white".concat(
                    route.pathname === "/provider_users/edit"
                      ? "text-white brightness-[85%]"
                      : "",
                  )}
                  style={{ backgroundColor: themeColor }}
                  onClick={() => navigate("/provider_users/edit")}
                >
                  Edit Profile
                </button>
              </div>
            ) : (
              <button
                className="mr-5 rounded px-4 py-2 text-white"
                style={{ backgroundColor: themeColor }}
                onClick={() => navigate("/provider_users/edit")}
              >
                Edit Profile
              </button>
            )}

            <button
              className="rounded px-4 py-2 text-white"
              style={{ backgroundColor: themeColor }}
              onClick={() => {
                localStorage.removeItem("Authorization");
                localStorage.removeItem("Email");
                updateMeData({});
                window.location.reload();
              }}
            >
              Logout {!isOctaPharma && localStorage.getItem("point_of_contact")}
            </button>
          </div>
        ) : (
          !PATHS_WITHOUT_LOGIN_BUTTON.includes(route.pathname) && (
            <button
              className="rounded px-4 py-2 text-white"
              style={{ backgroundColor: themeColor }}
              onClick={() => navigate("/signin")}
            >
              Provider Login
            </button>
          )
        )}
      </div>
    </>
  );
};

export default LayoutHeader;